import {
  AUTH_COOKIE,
  AUTH_SESSION_COOKIE,
  REFRESH_COOKIE,
  PARTNER_COOKIE,
  beginAuthRefreshAttempt,
  clearAuthCookies,
  endAuthRefreshAttempt,
  hasAuthLogoutIntent,
  hasOtherAuthRefreshAttempt,
  isAuthRefreshAttemptActive,
  isAuthRefreshAttemptFromCurrentGeneration,
  isSameJwtUser,
  readAuthSessionCookies,
  readCookie,
  readJwtSubject,
  setAuthSessionCookies,
  type AuthRefreshAttempt,
} from '@artic/ui/lib/auth-cookies';
import { getApiUrl } from '@artic/ui/lib/environment';

// A losing refresh response can arrive before the winning context receives its
// response and commits the rotated pair. Give normal cross-subdomain network
// skew time to settle before declaring the shared session dead. Tests use the
// same polling behavior with a shorter wall-clock delay.
const RACE_LOSS_POLL_DELAYS_MS =
  import.meta.env.MODE === 'test' ? [0, 10, 50] : [0, 50, 150, 300, 500, 1_000, 1_000];

// Single source of truth for every service URL: @artic/ui/lib/environment.
const getBackendUrl = getApiUrl;

/**
 * Build an absolute URL to a backend image-serving endpoint, for direct use as
 * an `<img src>`. The backend resolves + resizes the image server-side (GCS /
 * sharp, with a Supabase download fallback — never a billable Supabase image
 * transformation).
 *
 * The browser-issued `<img>` request authenticates via the `artic_token`
 * cookie: it's scoped to `.artic.works`, so it rides along same-site to the API
 * subdomain (SameSite=Lax). No token is placed in the URL — passing the JWT as a
 * `?auth=` query param leaked it via server logs, the `Referer` header, and
 * browser history. (The backend still reads the cookie first, as it always has.)
 *
 * @param relativePath Backend-relative path (e.g. `/quality/evidence/file/<partner>/<path>?w=100`).
 * @returns Absolute URL, or `undefined` when `relativePath` is falsy.
 */
export function buildAuthedImageUrl(relativePath: string | null | undefined): string | undefined {
  if (!relativePath) return undefined;
  return `${getBackendUrl()}${relativePath}`;
}

// ── Auth refresh configuration ──────────────────────────────────────

export interface AuthRefreshConfig {
  /** Cookie domain for writing refreshed tokens (e.g. `.artic.works`). */
  cookieDomain?: string | undefined;
  /** Called when token refresh fails and the user must re-authenticate. */
  onRefreshFailure?: (() => void) | undefined;
}

let authConfig: AuthRefreshConfig = {};

/** Configure auth refresh behaviour. Call once at app startup. */
export function configureAuth(config: AuthRefreshConfig): void {
  authConfig = config;
}

// ── Singleton refresh promise ───────────────────────────────────────

let refreshPromise: Promise<string | null> | null = null;

// ── Refresh-failed latch ────────────────────────────────────────────
//
// Once a refresh has failed for any reason (`refresh_token_already_used`
// from a race with another tab, Supabase 429, network blip, genuine
// invalid grant), every subsequent authenticated request must fail fast.
// Re-trying the same dead token only spams the auth log, racks up
// Supabase rate-limit hits, and risks a redirect loop. The page tears
// down to /login once and stays there until the user re-authenticates.

let refreshFailed = false;
let refreshFailureFired = false;

/**
 * Read the refresh-failed latch from outside the module. The hub's
 * Supabase client uses this to short-circuit `/auth/v1/token` POSTs at
 * the network layer once we've declared refresh dead — supabase-auth-js
 * has its own caller paths (auto-refresh ticker, Realtime reconnect,
 * direct `getSession()` from ~30 hub API helpers) that bypass this
 * api-client's `customFetch`. See Sentry ARTIC-HUB-12X.
 */
export function isAuthRefreshFailed(): boolean {
  return refreshFailed;
}

function cookieOptions(): { domain?: string } {
  return authConfig.cookieDomain ? { domain: authConfig.cookieDomain } : {};
}

function markRefreshFailed(): null {
  if (!refreshFailed) {
    refreshFailed = true;
    // Wipe the artic cookies so any code that still reads them between
    // failure and the consumer's navigation sees an unauthenticated state.
    clearAuthCookies(cookieOptions());
  }
  return null;
}

/**
 * Trip the refresh-failed latch from outside this module. The hub's
 * App.tsx calls this from its `SIGNED_OUT` handler (after the race-loss
 * bail) so a single supabase-side refresh failure short-circuits every
 * subsequent `/auth/v1/token` call via `guardedFetch` — even from tabs
 * whose own `customFetch` hasn't yet hit a 401. Without this bridge the
 * ~30 `getSession()` callsites in the hub each fire a real refresh
 * attempt before the latch trips locally; with it, the first failure
 * elsewhere takes the whole tab offline immediately.
 *
 * Idempotent. Also clears the projection cookies, matching the internal
 * `markRefreshFailed` semantics so consumers don't have to.
 */
export function tripAuthRefreshLatch(): void {
  markRefreshFailed();
}

/**
 * Clear the refresh-failed latch so `customFetch` will attempt refresh
 * again on the next 401. Call from the hub's `SIGNED_IN` /
 * `TOKEN_REFRESHED` handler when a valid session is established — the
 * latch persists across re-logins otherwise, leaving the user "signed
 * in" but unable to make authenticated requests until a full page
 * reload resets the module state.
 *
 * Also resets `refreshFailureFired` so `authConfig.onRefreshFailure`
 * can fire on the next round of failures.
 */
export function resetAuthRefreshLatch(): void {
  refreshFailed = false;
  refreshFailureFired = false;
}

/**
 * Race-loss check. When a refresh fails, the failure may be because some
 * concurrent path (supabase-auth-js's auto-refresh ticker, another tab on
 * a sibling subdomain) consumed the single-use refresh_token first. The
 * tell-tale signature: the cookie's refresh_token now differs from the
 * one we sent, AND a valid access_token is present. In that case the user
 * is still authenticated; tearing down to /login would be a regression.
 * Return the freshly-written access_token so customFetch retries with it.
 */
async function detectRaceLoss(
  attemptedRefreshToken: string,
  attemptedAccessToken: string | undefined,
  ownAttempt: AuthRefreshAttempt,
): Promise<string | null> {
  for (const delay of RACE_LOSS_POLL_DELAYS_MS) {
    if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
    const currentSession = readAuthSessionCookies();
    if (currentSession && currentSession.refreshToken !== attemptedRefreshToken) {
      if (
        !isAuthRefreshAttemptFromCurrentGeneration(ownAttempt) ||
        currentSession.previousRefreshToken !== attemptedRefreshToken ||
        !isSameJwtUser(attemptedAccessToken, currentSession.accessToken)
      ) {
        throw authSessionChangedError();
      }
      return currentSession.accessToken;
    }
  }

  // Instrumented refresh paths leave a short-lived owner marker. If another
  // owner is still active, keep waiting for its pair commit rather than using
  // an arbitrary network-latency cutoff to destroy the shared session.
  while (hasOtherAuthRefreshAttempt(attemptedRefreshToken, ownAttempt)) {
    await new Promise((resolve) => setTimeout(resolve, 250));
    const currentSession = readAuthSessionCookies();
    if (currentSession && currentSession.refreshToken !== attemptedRefreshToken) {
      if (
        !isAuthRefreshAttemptFromCurrentGeneration(ownAttempt) ||
        currentSession.previousRefreshToken !== attemptedRefreshToken ||
        !isSameJwtUser(attemptedAccessToken, currentSession.accessToken)
      ) {
        throw authSessionChangedError();
      }
      return currentSession.accessToken;
    }
  }
  return null;
}

async function readRefreshToken(): Promise<string | null> {
  // Existing sessions created before the commit cookie was introduced still
  // use the legacy refresh projection. Once a commit exists, never bypass it:
  // an invalid snapshot means another writer may be between cookie writes.
  if (!readCookie(AUTH_SESSION_COOKIE)) return readCookie(REFRESH_COOKIE) ?? null;

  for (const delay of RACE_LOSS_POLL_DELAYS_MS) {
    if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
    const session = readAuthSessionCookies();
    if (session) return session.refreshToken;
  }
  return null;
}

async function attemptRefresh(): Promise<string | null> {
  if (hasAuthLogoutIntent()) return markRefreshFailed();
  const attemptedAccessToken = readCookie(AUTH_COOKIE);
  const refreshToken = await readRefreshToken();
  if (!refreshToken) return markRefreshFailed();
  const opts = cookieOptions();
  const refreshAttempt = beginAuthRefreshAttempt(refreshToken, opts);

  try {
    const response = await fetch(`${getBackendUrl()}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      const winner = await detectRaceLoss(refreshToken, attemptedAccessToken, refreshAttempt);
      return winner ?? markRefreshFailed();
    }

    const result = (await response.json()) as Record<string, unknown>;
    const accessToken = result['access_token'];
    const nextRefreshToken = result['refresh_token'];
    if (
      typeof accessToken !== 'string' ||
      accessToken.length === 0 ||
      readJwtSubject(accessToken) === null ||
      typeof nextRefreshToken !== 'string' ||
      nextRefreshToken.length === 0 ||
      (readJwtSubject(attemptedAccessToken) !== null &&
        !isSameJwtUser(attemptedAccessToken, accessToken))
    ) {
      throw authSessionChangedError();
    }

    if (
      !isAuthRefreshAttemptActive(refreshAttempt) ||
      !isAuthRefreshAttemptFromCurrentGeneration(refreshAttempt)
    ) {
      const winner = readAuthSessionCookies();
      if (winner && winner.refreshToken !== refreshToken) {
        if (
          !isAuthRefreshAttemptFromCurrentGeneration(refreshAttempt) ||
          winner.previousRefreshToken !== refreshToken ||
          !isSameJwtUser(attemptedAccessToken, winner.accessToken)
        ) {
          throw authSessionChangedError();
        }
        return winner.accessToken;
      }
      return markRefreshFailed();
    }

    setAuthSessionCookies(accessToken, nextRefreshToken, opts, refreshToken);

    return accessToken;
  } catch {
    const winner = await detectRaceLoss(refreshToken, attemptedAccessToken, refreshAttempt);
    return winner ?? markRefreshFailed();
  } finally {
    endAuthRefreshAttempt(refreshAttempt, opts);
  }
}

function refreshOnce(): Promise<string | null> {
  if (refreshFailed) return Promise.resolve(null);
  if (!refreshPromise) {
    refreshPromise = attemptRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// ── Proactive refresh ──────────────────────────────────────────────

/**
 * Ensures the user has a valid access token, refreshing proactively if
 * needed.  Call from route guards / `beforeLoad` to avoid the 401 →
 * refresh → retry round-trip when the access token cookie has expired
 * but the refresh token is still present.
 *
 * Returns `true` if an access token is available (existing or freshly
 * refreshed), `false` if refresh failed and the user must re-authenticate.
 */
export async function ensureAuthenticated(): Promise<boolean> {
  if (refreshFailed) return false;

  const token = readCookie(AUTH_COOKIE);
  if (token) return true;

  const newToken = await refreshOnce();
  return newToken !== null;
}

// ── Structured error ───────────────────────────────────────────────
//
// customFetch throws an ApiError on non-2xx so callers can branch on
// status + parsed body without grepping a stringified message. The body
// is the backend's JSON response when parseable (typically
// `{ statusCode, message, error?, field? }` for Nest exceptions), or
// raw text when the response isn't JSON.

export class ApiError extends Error {
  /** HTTP status code. */
  public readonly status: number;
  /** HTTP method + path that failed, for diagnostics. */
  public readonly method: string;
  public readonly url: string;
  /** Parsed JSON body when available; raw string otherwise; undefined for empty. */
  public readonly body: unknown;

  constructor(params: {
    status: number;
    method: string;
    url: string;
    body: unknown;
    message?: string;
  }) {
    super(params.message ?? `${params.method} ${params.url} failed: ${String(params.status)}`);
    this.name = 'ApiError';
    this.status = params.status;
    this.method = params.method;
    this.url = params.url;
    this.body = params.body;
  }

  /**
   * Backend exception body shape: Nest's HttpException returns an object
   * response, so callers can pull fields like `message` / `field` out in
   * one place instead of reparsing the message string.
   */
  get bodyAsRecord(): Record<string, unknown> | null {
    if (this.body && typeof this.body === 'object') {
      return this.body as Record<string, unknown>;
    }
    return null;
  }
}

function authSessionChangedError(): ApiError {
  return new ApiError({
    status: 409,
    method: 'POST',
    url: '/auth/refresh',
    body: { message: 'auth_session_changed' },
    message: 'Authentication session changed while refresh was in flight.',
  });
}

// ── Request building (shared by the JSON + blob fetchers) ────────────

function buildRequestHeaders(options: RequestInit, token: string | undefined): HeadersInit {
  const partnerName = readCookie(PARTNER_COOKIE);
  // A FormData body is a multipart upload: the browser must set the
  // `Content-Type: multipart/form-data; boundary=…` header itself, so we must
  // NOT declare one (any explicit value suppresses the boundary and the server
  // fails to parse the parts). All other bodies are JSON.
  const isMultipart = typeof FormData !== 'undefined' && options.body instanceof FormData;
  return {
    // Only declare a JSON content-type when we're actually sending a (non-
    // multipart) body. Fastify's JSON parser rejects an empty body when
    // Content-Type is application/json ("Body cannot be empty when content-type
    // is set to 'application/json'"), which 400s bodyless POST/DELETE calls —
    // e.g. the custom-column restore/delete endpoints — before they reach the
    // handler.
    ...(options.body != null && !isMultipart ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(partnerName ? { partner_name: partnerName } : {}),
  };
}

async function toApiError(response: Response, method: string, url: string): Promise<ApiError> {
  const rawBody = await response.text().catch(() => '');
  let parsedBody: unknown = rawBody;
  // Most backend errors respond with JSON (NestJS standard shape). Fall
  // back to the raw text when parsing fails so diagnostics still make it
  // to the caller.
  if (rawBody) {
    try {
      parsedBody = JSON.parse(rawBody);
    } catch {
      parsedBody = rawBody;
    }
  } else {
    parsedBody = undefined;
  }

  // Prefer the backend's own message when present so the toast / inline
  // error carries the exact copy the service emitted.
  const record =
    parsedBody && typeof parsedBody === 'object' ? (parsedBody as Record<string, unknown>) : null;
  const message =
    record && typeof record['message'] === 'string'
      ? (record['message'] as string)
      : `${method} ${url} failed: ${String(response.status)}`;

  return new ApiError({ status: response.status, method, url, body: parsedBody, message });
}

// ── Custom fetch mutator ────────────────────────────────────────────

async function doFetch<T>(
  url: string,
  options: RequestInit,
  token: string | undefined,
): Promise<T> {
  const response = await fetch(`${getBackendUrl()}${url}`, {
    ...options,
    headers: buildRequestHeaders(options, token),
  });

  const method = options.method ?? 'GET';
  if (!response.ok) throw await toApiError(response, method, url);

  // Parse body (handle 204 No Content)
  const text = await response.text();
  const data = text ? JSON.parse(text) : undefined;

  return { data, status: response.status, headers: response.headers } as T;
}

async function doFetchBlob(
  url: string,
  options: RequestInit,
  token: string | undefined,
): Promise<Blob> {
  const response = await fetch(`${getBackendUrl()}${url}`, {
    ...options,
    headers: buildRequestHeaders(options, token),
  });

  const method = options.method ?? 'GET';
  if (!response.ok) throw await toApiError(response, method, url);

  return response.blob();
}

/**
 * Wrap a fetcher with the shared auth lifecycle: fail fast once refresh is
 * declared dead, inject the access token, and transparently refresh + retry
 * once on a 401. Used by both customFetch (JSON) and fetchBlob (binary) so
 * the two behave identically around auth.
 */
async function withAuthRetry<T>(
  url: string,
  options: RequestInit,
  run: (url: string, options: RequestInit, token: string | undefined) => Promise<T>,
): Promise<T> {
  // Fail fast once refresh has been declared dead. Saves the wasted
  // request, keeps the auth log quiet while the consumer tears the
  // page down to /login.
  if (refreshFailed) {
    throw new ApiError({
      status: 401,
      method: options.method ?? 'GET',
      url,
      body: { message: 'auth_refresh_failed' },
      message: 'Authentication refresh has failed; sign in again.',
    });
  }

  const token = readCookie(AUTH_COOKIE);

  try {
    return await run(url, options, token);
  } catch (error) {
    // Only attempt refresh on 401 errors
    if (!(error instanceof ApiError) || error.status !== 401) {
      throw error;
    }

    const newToken = await refreshOnce();

    if (!newToken) {
      // Concurrent 401s all land here; only the first call gets to
      // notify the consumer so we don't fire navigation N times.
      if (!refreshFailureFired) {
        refreshFailureFired = true;
        authConfig.onRefreshFailure?.();
      }
      throw error;
    }

    // Retry with the refreshed token
    return run(url, options, newToken);
  }
}

/**
 * Custom fetch mutator for Orval-generated clients.
 *
 * Handles base URL resolution, auth token injection, and transparent
 * 401 retry via the backend's /auth/refresh endpoint.
 *
 * Orval expects the mutator to return `{ data, status, headers }`.
 */
export const customFetch = <T>(url: string, options: RequestInit): Promise<T> =>
  withAuthRetry<T>(url, options, (u, o, t) => doFetch<T>(u, o, t));

/**
 * Authenticated binary download. Same base-URL resolution, auth header,
 * partner scoping, and 401-refresh-retry as customFetch, but resolves to the
 * response Blob — customFetch always JSON-parses the body, so it can't carry
 * a PDF / file download. Pass `{ method, body }` exactly like customFetch.
 */
export const fetchBlob = (url: string, options: RequestInit = {}): Promise<Blob> =>
  withAuthRetry<Blob>(url, options, doFetchBlob);

export default customFetch;
